/**
 * Shared global state for music management
 * Ensures that only one track plays at a time across all music hooks
 */

// Global reference to currently playing audio (only one at a time)
export let currentPlayingAudio = null;

// Global promise that tracks the current fade-out operation
export let currentFadeOutPromise = Promise.resolve();

// Lock to prevent concurrent track starts
let trackStartLock = false;
let trackStartLockPromise = Promise.resolve();

// Track all known audio elements to ensure we can stop them all
const allAudioElements = new Set();

// Track audio state before pause (for screen lock resume)
let audioStateBeforePause = {
  currentAudio: null,
  wasPlaying: false,
  volume: 0,
  currentTime: 0
};

/** Ref-count per track path so the same menu theme keeps playing across route changes. */
const trackRetainCounts = new Map();
/** Delayed fade-out when the last holder unmounts (allows the next screen to retain). */
const pendingTrackStopTimeouts = new Map();

export function incrementTrackRetain(trackPath) {
  if (!trackPath) return 0;
  const next = (trackRetainCounts.get(trackPath) || 0) + 1;
  trackRetainCounts.set(trackPath, next);
  cancelScheduledTrackStop(trackPath);
  return next;
}

export function decrementTrackRetain(trackPath) {
  if (!trackPath) return 0;
  const next = Math.max(0, (trackRetainCounts.get(trackPath) || 0) - 1);
  if (next === 0) {
    trackRetainCounts.delete(trackPath);
  } else {
    trackRetainCounts.set(trackPath, next);
  }
  return next;
}

export function getTrackRetainCount(trackPath) {
  if (!trackPath) return 0;
  return trackRetainCounts.get(trackPath) || 0;
}

export function cancelScheduledTrackStop(trackPath) {
  const pending = pendingTrackStopTimeouts.get(trackPath);
  if (pending) {
    clearTimeout(pending);
    pendingTrackStopTimeouts.delete(trackPath);
  }
}

/**
 * Run stopFn only if no component still holds this track after a short delay.
 * @param {string} trackPath
 * @param {() => void} stopFn
 * @param {number} delayMs
 */
export function scheduleTrackStopIfUnretained(trackPath, stopFn, delayMs = 100) {
  if (!trackPath) return;
  cancelScheduledTrackStop(trackPath);
  const timeoutId = setTimeout(() => {
    pendingTrackStopTimeouts.delete(trackPath);
    if (getTrackRetainCount(trackPath) === 0) {
      stopFn();
    }
  }, delayMs);
  pendingTrackStopTimeouts.set(trackPath, timeoutId);
}

/**
 * Register an audio element for tracking
 * @param {HTMLAudioElement} audio - The audio element to register
 */
export function registerAudioElement(audio) {
  if (audio) {
    allAudioElements.add(audio);
  }
}

/**
 * Unregister an audio element
 * @param {HTMLAudioElement} audio - The audio element to unregister
 */
export function unregisterAudioElement(audio) {
  if (audio) {
    allAudioElements.delete(audio);
  }
}

/**
 * Stop ALL audio elements immediately (emergency stop)
 * This is a safety measure to prevent multiple tracks playing
 */
export function stopAllAudio() {
  // Stop the tracked current audio
  if (currentPlayingAudio) {
    try {
      currentPlayingAudio.pause();
      currentPlayingAudio.currentTime = 0;
      currentPlayingAudio.volume = 0;
    } catch (e) {
      console.warn('Error stopping current audio:', e);
    }
  }

  // Stop ALL registered audio elements
  allAudioElements.forEach(audio => {
    try {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
      }
    } catch (e) {
      console.warn('Error stopping audio element:', e);
    }
  });

  // Also check document for any audio elements (defensive)
  try {
    const allAudioInDOM = document.querySelectorAll('audio');
    allAudioInDOM.forEach(audio => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
      }
    });
  } catch (e) {
    console.warn('Error stopping DOM audio elements:', e);
  }
}

/**
 * Acquire lock for starting a new track
 * @returns {Promise} Promise that resolves when lock is acquired
 */
export async function acquireTrackStartLock() {
  // Wait for any existing lock to be released
  await trackStartLockPromise;
  
  // Wait for any ongoing fade-out
  await currentFadeOutPromise;
  
  // Stop ALL audio before acquiring lock (safety measure)
  stopAllAudio();
  
  // Create new lock promise
  let releaseLock;
  trackStartLockPromise = new Promise(resolve => {
    releaseLock = resolve;
  });
  
  trackStartLock = true;
  
  return () => {
    trackStartLock = false;
    releaseLock();
  };
}

/**
 * Check if track start is locked
 * @returns {boolean} True if locked
 */
export function isTrackStartLocked() {
  return trackStartLock;
}

/**
 * Set the currently playing audio
 * @param {HTMLAudioElement|null} audio - The audio element to set as current
 */
export function setCurrentPlayingAudio(audio) {
  // Before setting new audio, ensure old one is stopped
  if (currentPlayingAudio && currentPlayingAudio !== audio) {
    try {
      currentPlayingAudio.pause();
      currentPlayingAudio.currentTime = 0;
      currentPlayingAudio.volume = 0;
    } catch (e) {
      console.warn('Error stopping previous audio:', e);
    }
  }
  
  currentPlayingAudio = audio;
  
  // Register/unregister for tracking
  if (audio) {
    registerAudioElement(audio);
  }
}

/**
 * Get the currently playing audio
 * @returns {HTMLAudioElement|null} The currently playing audio
 */
export function getCurrentPlayingAudio() {
  return currentPlayingAudio;
}

/**
 * Set the current fade-out promise
 * @param {Promise} promise - The fade-out promise
 */
export function setCurrentFadeOutPromise(promise) {
  currentFadeOutPromise = promise;
}

/**
 * Get the current fade-out promise
 * @returns {Promise} The current fade-out promise
 */
export function getCurrentFadeOutPromise() {
  return currentFadeOutPromise;
}

/**
 * Periodic safety check to ensure only one track is playing
 * This is a defensive measure to catch any edge cases
 */
let safetyCheckInterval = null;

export function startSafetyCheck() {
  if (safetyCheckInterval) return; // Already running
  
  safetyCheckInterval = setInterval(() => {
    try {
      const currentAudio = getCurrentPlayingAudio();
      let playingCount = 0;
      let playingAudios = [];
      
      // Check all registered audio elements
      allAudioElements.forEach(audio => {
        if (audio && !audio.paused && audio.volume > 0) {
          playingCount++;
          playingAudios.push(audio);
        }
      });
      
      // If more than one is playing, stop all except the current one
      if (playingCount > 1) {
        console.warn(`⚠️ Music Safety: Detected ${playingCount} tracks playing simultaneously. Stopping extras.`);
        playingAudios.forEach(audio => {
          if (audio !== currentAudio) {
            try {
              audio.pause();
              audio.currentTime = 0;
              audio.volume = 0;
            } catch (e) {
              console.warn('Error stopping duplicate audio in safety check:', e);
            }
          }
        });
      }
    } catch (e) {
      console.warn('Error in music safety check:', e);
    }
  }, 1000); // Check every second
}

export function stopSafetyCheck() {
  if (safetyCheckInterval) {
    clearInterval(safetyCheckInterval);
    safetyCheckInterval = null;
  }
}

/**
 * Pause all audio (for screen lock/visibility change)
 * Remembers the state so we can resume later
 */
export function pauseAllAudio() {
  // Store state before pausing
  if (currentPlayingAudio && !currentPlayingAudio.paused) {
    audioStateBeforePause = {
      currentAudio: currentPlayingAudio,
      wasPlaying: true,
      volume: currentPlayingAudio.volume,
      currentTime: currentPlayingAudio.currentTime
    };
    
    // Pause the current audio
    try {
      currentPlayingAudio.pause();
    } catch (e) {
      console.warn('Error pausing audio for screen lock:', e);
    }
  } else {
    audioStateBeforePause = {
      currentAudio: null,
      wasPlaying: false,
      volume: 0,
      currentTime: 0
    };
  }
  
  // Pause all registered audio elements
  allAudioElements.forEach(audio => {
    try {
      if (audio && !audio.paused) {
        audio.pause();
      }
    } catch (e) {
      console.warn('Error pausing audio element for screen lock:', e);
    }
  });
  
  // Also pause any audio in DOM
  try {
    const allAudioInDOM = document.querySelectorAll('audio');
    allAudioInDOM.forEach(audio => {
      if (audio && !audio.paused) {
        audio.pause();
      }
    });
  } catch (e) {
    console.warn('Error pausing DOM audio elements for screen lock:', e);
  }
}

/**
 * Resume audio that was playing before pause (for screen unlock/visibility change)
 */
export function resumeAllAudio() {
  // Only resume if we had audio playing before
  if (audioStateBeforePause.currentAudio && audioStateBeforePause.wasPlaying) {
    const audio = audioStateBeforePause.currentAudio;
    
    try {
      // Restore volume and position
      audio.volume = audioStateBeforePause.volume;
      audio.currentTime = audioStateBeforePause.currentTime;
      
      // Try to resume playback
      audio.play().catch(error => {
        // Handle autoplay restrictions (common on mobile after screen unlock)
        if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
          console.log('Audio resume blocked after screen unlock. Waiting for user interaction...');
          // The health check in music hooks will handle restart on next user interaction
        } else {
          console.error('Error resuming audio after screen unlock:', error);
        }
      });
    } catch (e) {
      console.warn('Error resuming audio after screen unlock:', e);
    }
  }
  
  // Reset state
  audioStateBeforePause = {
    currentAudio: null,
    wasPlaying: false,
    volume: 0,
    currentTime: 0
  };
}

// Start safety check automatically
if (typeof window !== 'undefined') {
  startSafetyCheck();
}

